<template>
  <section>
    <b-modal v-model="showStatus"
           @ok="submitData"
           :ok-disabled="isLoading">
      <div slot="modal-title">
        {{ $t('message.button.select_service_datetime') }}
        <span v-if="order">#{{ order.order_code }}</span>
      </div>
      <section class="pb-3">
        <section v-show="!isSelectingUserPlace && !isSelectingProfessional">
          <!-- User place selection -->
          <div class="mb-3">
            <div>
              <strong>{{ $t('message.no_datetime_booking.select_service_location') }}</strong>
            </div>
            <div class="d-flex border border-divider justify-content-start align-items-center bg-white rounded p-2 mt-1">
              <div class="px-2">
                <i class="fas fa-home-alt fa-lg"></i>
              </div>
              <div class="flex-fill px-2" v-if="selectedUserPlace">
                <div class="text-small">
                  <strong>{{ selectedUserPlace.place_name }}</strong>
                </div>
                <div class="text-small text-secondary">
                  {{ selectedUserPlace.address }}
                  <span v-if="selectedUserPlace.sub_district">{{ selectedUserPlace.sub_district | multiLangName }}</span>
                  {{ selectedUserPlace.district | multiLangName }}
                  {{ selectedUserPlace.address_province | multiLangName }}
                  <span v-if="selectedUserPlace.sub_district">{{ selectedUserPlace.sub_district.zip_code }}</span>
                </div>
              </div>
              <div>
                <button class="btn btn-outline-info" 
                      @click="goToSelectUserPlace()">
                  {{ $t('message.button.change') }}
                </button>
              </div>
            </div>
          </div>
          <!-- Professional selection -->
          <div class="d-flex">
            <div class="flex-fill"><strong>{{ $t('message.no_datetime_booking.select_service_provider') }}</strong></div>
            <div>
              <a href="" class="text-danger" 
                  @click.prevent="clearSelectedProfessional()" 
                  v-if="selectedProfessional">{{ $t('message.service_item.clear') }}</a>
            </div>
          </div>
          <div class="d-flex border border-divider justify-content-start align-items-center bg-white rounded p-2 mt-1" >
            <div>
              <professional-picture :professional="selectedProfessional" :imgSize="40" v-if="selectedProfessional"/>
              <img src="https://beneat.co/images/ic_unknown.png" class="rounded-circle" width="40" v-else/>
            </div>
            <div class="flex-fill pl-2">
              <span v-if="selectedProfessional">{{ selectedProfessional.name | firstName }}</span>
              <span v-else>{{ $t('message.repeat_booking.booking_any_provider') }}</span>
            </div>
            <div>
              <button class="btn btn-outline-info" 
                      @click="goToSelectProfessional()">
                {{ $t('message.button.change') }}
              </button>
            </div>
          </div>
        </section>
        
        <div v-show="isSelectingUserPlace || isSelectingProfessional">
          <div class="mb-3">
            <a href="" class="text-dark text-underline" @click.prevent="clearIsSelectingData">
              <i class="fas fa-angle-left"></i> {{ $t('message.booking.new_booking.back') }}
            </a>
          </div>
          
          <div v-show="isSelectingUserPlace">
            <select-user-place ref="selectUserPlaceComp" 
                              @onSelected="setSelectedUserPlace"
                              :order="order"></select-user-place>
          </div>
          
          <div v-show="isSelectingProfessional">
            <div v-show="selectProfessionalStep === 1">
              <a href="" @click.prevent="selectProfessionalType('any')">
                <div class="row border border-info rounded-pill p-2 mb-3">
                  <div class="col-1">
                    <img src="/static/img/select-any-professional.svg" width="24">
                  </div>
                  <div class="col-10 text-dark">{{ $t('message.no_datetime_booking.choose_any_professional') }}</div>
                  <div class="col-1">
                    <i class="fal fa-angle-right text-info"></i>
                  </div>
                </div>
              </a>

              <a href="" @click.prevent="selectProfessionalType('usedService')">
                <div class="row border border-info rounded-pill p-2 mb-3">
                  <div class="col-1">
                    <img src="/static/img/select-used-professional.svg" width="24">
                  </div>
                  <div class="col-10 text-dark">{{ $t('message.no_datetime_booking.choose_used_professional') }}</div>
                  <div class="col-1">
                    <i class="fal fa-angle-right text-info"></i>
                  </div>
                </div>
              </a>

              <a href="" @click.prevent="selectProfessionalType('new')">
                <div class="row border border-info rounded-pill p-2 mb-3">
                  <div class="col-1">
                    <img src="/static/img/select-new-professional.svg" width="24">
                  </div>
                  <div class="col-10 text-dark">{{ $t('message.no_datetime_booking.choose_new_professional') }}</div>
                  <div class="col-1">
                    <i class="fal fa-angle-right text-info"></i>
                  </div>
                </div>
              </a>
            </div>
            
            <div v-show="selectProfessionalStep === 2">
              <select-professional ref="selectProfessionalComp" 
                              @onSelected="setSelectedProfessional"
                              :userPlace="selectedUserPlace" 
                              :order="order"></select-professional>
            </div>
            
          </div>
          
        </div>
      </section>
      <!-- Date-Time Selection -->
      <section v-show="!isSelectingUserPlace && !isSelectingProfessional">
        <div class="pb-2">
          <strong>{{ $t('message.no_datetime_booking.select_service_datetime') }}</strong>
        </div>
        <div v-show="!isLoadingProfessionalCalendar">
          <div class="multiDatesCalendar datepicker-here"></div>
        </div>
        <loader v-if="isLoadingProfessionalCalendarJobs || isLoadingProfessionalCalendar"></loader>
        <div class="row no-gutters mt-2" v-if="availableTimes">
          <div class="col-md-3 col-4 p-1" v-for="time, index in availableTimes">
            <button class="btn btn-outline-info btn-block btn-small-group text-center"
                    :class="{'active': selectedTime === time}"
                    @click="setSelectedTime(time)">{{ time }}
            </button>
          </div>
        </div>
      </section>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          {{ $t('message.button.cancel') }}
        </b-button>
        <b-button variant="info" @click="ok()" :disabled="isInvalid">
          {{ isLoading ? $t('message.button.loading') : $t('message.button.ok') }}
        </b-button>
      </template>

    </b-modal>

  </section>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import ProfessionalPicture from '../../professional/components/ProfessionalPicture'
  import SelectProfessional from './SelectProfessional'
  import SelectUserPlace from './SelectUserPlace'
  import Loader from '../../loader/Loader'
  import axios from 'axios'
  import moment from 'moment-timezone'
  import timeHelpers from '../../../helpers/timeHelpers'
  import serviceHelpers from '../../../helpers/serviceHelpers'

  export default {
    props: {
      order: Object
    },
    components: {
      Loader,
      ProfessionalPicture,
      SelectProfessional,
      SelectUserPlace
    },
    data () {
      return {
        showStatus: false,
        parentServiceId: null,
        isLoading: false,
        language: null,
        selectedDate: null,
        selectedTime: null,
        datePicker: null,
        professionalCalendarJobs: null,
        availableTimes: [],
        fullBlockedDates: [],
        partialBlockedDates: [],
        cleaningDates: [],
        fullyBookedDates: [],
        sameDayBookingConfig: null,
        isSelectingUserPlace: false,
        isSelectingProfessional: false,
        isLoadingProfessionalCalendar: false,
        isLoadingProfessionalCalendarJobs: false,
        selectedProfessional: null,
        selectedUserPlace: null,
        selectProfessionalStep: 1
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        user: 'auth/getUser'
      }),
      isInvalid () {
        let invalid = false
        if (this.isSelectingProfessional || this.selectedDate === null || this.selectedTime === null || this.isLoading) {
          invalid = true
        }
        return invalid
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      resetData () {
        this.serviceDate = null
        this.serviceTime = null
        this.selectedProfessional = null
        this.availableTimes = []
        this.fullBlockedDates = []
        this.partialBlockedDates = []
        this.cleaningDates = []
        this.fullyBookedDates = []
      },
      showModal (status) {
        this.showStatus = status
        if (status) {
          // Set user place from order detail
          if (this.order && this.order.user_place) {
            this.selectedUserPlace = this.order.user_place
          }
          this.isSelectingUserPlace = false
          this.isSelectingProfessional = false
          this.selectedProfessional = null
          this.resetData()
          setTimeout(() => { this.initDatePicker() }, 500)
        }
      },
      selectProfessionalType (professionalType) {
        if (professionalType === 'any') {
          this.isSelectingProfessional = false
          this.resetData()
        } else {
          this.selectProfessionalStep = 2
          this.$refs.selectProfessionalComp.init(professionalType)
        }
      },
      clearIsSelectingData () {
        this.isSelectingProfessional = false
        this.isSelectingUserPlace = false
      },
      goToSelectUserPlace () {
        this.isSelectingUserPlace = true
        this.$refs.selectUserPlaceComp.init()
      },
      goToSelectProfessional () {
        this.isSelectingProfessional = true
        this.resetData()
        this.selectProfessionalStep = 1
      },
      setSelectedUserPlace (userPlace) {
        this.isSelectingUserPlace = false
        this.selectedUserPlace = userPlace
        this.clearSelectedProfessional()
      },
      setSelectedProfessional (professional) {
        this.isSelectingProfessional = false
        this.selectedProfessional = professional
        // Init calendar
        this.fetchProfessionalCalendar()
        // Clear selected date-time
        this.serviceDate = null
        this.serviceTime = null
        this.availableTimes = []
        if (this.datePicker && this.datePicker.data('datepicker')) {
          this.datePicker.data('datepicker').clear()
        }
      },
      clearSelectedProfessional () {
        this.selectedProfessional = null
        this.resetData()
        if (this.datePicker && this.datePicker.data('datepicker')) {
          this.datePicker.data('datepicker').clear()
        }
      },
      initDatePicker () {
        try {
          if (this.parentServiceId && this.sameDayBookingConfig.services) {
            this.sameDayBookingConfig = serviceHelpers.getSameDayService(this.sameDayBookingConfig, this.parentServiceId, this.order.province_id)
          }
          this.setDatePickerLanguage()
          const currentHour = Number(moment().format('H'))
          let minDate = moment()
          let amount = 0
          if (this.selectedProfessional) {
            amount = (currentHour >= this.sameDayBookingConfig.can_book_to_hour) ? 2 : 1
          } else {
            const canBookToHour = this.sameDayBookingConfig.can_book_to_hour
            amount = 1
            if (this.sameDayBookingConfig.can_book_same_day) {
              amount = 0
            } else {
              amount = (this.sameDayBookingConfig.num_next_day) ? this.sameDayBookingConfig.num_next_day : 1
            }
            if (currentHour >= canBookToHour) {
              amount += 1
            }
          }
          if (amount > 0) {
            minDate = moment().add(amount, 'days')
          }
          this.datePicker = window.$('.multiDatesCalendar').datepicker({
            language: this.language,
            minDate: minDate.toDate(),
            onSelect: (fmDate, datePicker) => {
              // Don't change condition below
              if (fmDate !== null && fmDate !== '') {
                this.selectedDate = fmDate
                this.selectedTime = null
                if (this.selectedProfessional) {
                  this.fetchProfessionalCalendarJobs(this.selectedDate, datePicker)
                } else {
                  const today = moment().format('YYYY-MM-DD')
                  if (this.selectedDate === today) {
                    let nextAvailableTime = timeHelpers.getNextAvailableFullTime()
                    if (currentHour < 7) {
                      nextAvailableTime = '10:00'
                    }
                    this.setAvailableTimes(nextAvailableTime)
                  } else {
                    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD')
                    if (this.selectedDate === tomorrow && currentHour >= 20) {
                      const nextAvailableTime = '10:00'
                      this.setAvailableTimes(nextAvailableTime)
                    } else {
                      this.availableTimes = this.globalState.arrFullTimes
                    }
                  }
                }
              } else {
                this.selectedDate = null
                this.selectedTime = null
                this.availableTimes = []
              }
            },
            onRenderCell: (date, cellType) => {
              const currentDate = date.getDate()
              const fmDate = moment(date).format('YYYY-MM-DD')
              if (cellType === 'day' && (this.fullBlockedDates.indexOf(fmDate) !== -1 || this.fullyBookedDates.indexOf(fmDate) !== -1)) {
                return {
                  disabled: true
                }
              } else if (cellType === 'day' && this.cleaningDates.indexOf(fmDate) !== -1) {
                return {
                  html: currentDate + '<span class="dp-note"></span>'
                }
              }
            }
          })
        } catch (e) {
          console.log('check error', e.message)
        }
      },
      setDatePickerLanguage () {
        window.$.fn.datepicker.language['th'] = {
          days: [this.$i18n.t('message.calendar.week.01'), this.$i18n.t('message.calendar.week.02'), this.$i18n.t('message.calendar.week.03'), this.$i18n.t('message.calendar.week.04'), this.$i18n.t('message.calendar.week.05'), this.$i18n.t('message.calendar.week.06'), this.$i18n.t('message.calendar.week.07')],
          daysShort: [this.$i18n.t('message.calendar.week_short.01'), this.$i18n.t('message.calendar.week_short.02'), this.$i18n.t('message.calendar.week_short.03'), this.$i18n.t('message.calendar.week_short.04'), this.$i18n.t('message.calendar.week_short.05'), this.$i18n.t('message.calendar.week_short.06'), this.$i18n.t('message.calendar.week_short.07')],
          daysMin: [this.$i18n.t('message.calendar.week_short.01'), this.$i18n.t('message.calendar.week_short.02'), this.$i18n.t('message.calendar.week_short.03'), this.$i18n.t('message.calendar.week_short.04'), this.$i18n.t('message.calendar.week_short.05'), this.$i18n.t('message.calendar.week_short.06'), this.$i18n.t('message.calendar.week_short.07')],
          months: [this.$i18n.t('message.calendar.month.01'), this.$i18n.t('message.calendar.month.02'), this.$i18n.t('message.calendar.month.03'), this.$i18n.t('message.calendar.month.04'), this.$i18n.t('message.calendar.month.05'), this.$i18n.t('message.calendar.month.06'), this.$i18n.t('message.calendar.month.07'), this.$i18n.t('message.calendar.month.08'), this.$i18n.t('message.calendar.month.09'), this.$i18n.t('message.calendar.month.10'), this.$i18n.t('message.calendar.month.11'), this.$i18n.t('message.calendar.month.12')],
          monthsShort: [this.$i18n.t('message.calendar.month_short.01'), this.$i18n.t('message.calendar.month_short.02'), this.$i18n.t('message.calendar.month_short.03'), this.$i18n.t('message.calendar.month_short.04'), this.$i18n.t('message.calendar.month_short.05'), this.$i18n.t('message.calendar.month_short.06'), this.$i18n.t('message.calendar.month_short.07'), this.$i18n.t('message.calendar.month_short.08'), this.$i18n.t('message.calendar.month_short.09'), this.$i18n.t('message.calendar.month_short.10'), this.$i18n.t('message.calendar.month_short.11'), this.$i18n.t('message.calendar.month_short.12')],
          today: this.$i18n.t('message.calendar.today'),
          clear: 'Clear',
          dateFormat: 'yyyy-mm-dd',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }

        window.$.fn.datepicker.language['en'] = {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'mm/dd/yyyy',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      },
      setSelectedTime (time) {
        this.selectedTime = time
      },
      parseTime (s) {
        const c = s.split(':')
        return parseInt(c[0]) * 60 + parseInt(c[1])
      },
      isPartialBlockedDate (selectedDate) {
        const fmDate = moment(selectedDate).format('YYYY-MM-DD')
        for (let key in this.partialBlockedDates) {
          const bDate = this.partialBlockedDates[key]
          if (bDate.date === fmDate) {
            return {
              startTime: bDate.start_time,
              endTime: bDate.end_time
            }
          }
        }
        return null
      },
      getAvailableTimes (selectedDate) {
        let startTime = null
        let endTime = null
        let availableTimes = []
        const partialBlockedDate = this.isPartialBlockedDate(selectedDate)
        if (this.partialBlockedDates.length > 0 && partialBlockedDate) {
          startTime = partialBlockedDate.startTime
          endTime = partialBlockedDate.endTime
        }
        const allTimes = this.globalState.arrTimes
        if (startTime == null && endTime == null) {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        } else {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (time >= startTime && time <= endTime && this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        }
        return availableTimes
      },
      checkAvailableTime (selectedDate, startTime, availableEndTime) {
        let result = true
        if (this.professionalCalendarJobs) {
          const hours = Number(this.workHours) + 0.5
          const endTime = moment(selectedDate + ' ' + startTime).add(hours, 'hours').format('HH:mm')
          for (let job of this.professionalCalendarJobs) {
            if (startTime >= job.start_time && startTime <= job.end_time) {
              result = false
              break
            } else {
              const minutes = this.parseTime(startTime) - this.parseTime(job.end_time)
              if (startTime < job.start_time && endTime >= job.start_time) {
                result = false
                break
              } else if (startTime > job.end_time && minutes < 60) {
                result = false
                break
              }
            }
          }
        }
        // Check available end time comparing with duration if the selected date is partial blocked date
        if (result === true && availableEndTime) {
          const minutes = this.parseTime(availableEndTime) - this.parseTime(startTime)
          const endMinute = 60 * Number(this.workHours)
          if (minutes < endMinute) {
            result = false
          }
        }
        return result
      },
      async fetchProfessionalCalendar () {
        this.isLoadingProfessionalCalendar = true
        let response = await axios.get('professional/calendars', {
          params: {
            professional_id: this.selectedProfessional.id,
            start_date: moment().format('YYYY-MM-DD'),
            end_date: null
          }
        })
        if (!response.data.error) {
          await this.fetchSoldOutDates()
          // Blocked dates
          const blockedDates = response.data.blocked_dates
          for (let key in blockedDates) {
            const bDate = blockedDates[key]
            if (bDate.blocked_type === 'full') {
              this.fullBlockedDates.push(bDate.date)
            } else if (bDate.blocked_type === 'partial') {
              this.partialBlockedDates.push(bDate)
            }
          }
          // Cleaning dates
          const cleaningDates = response.data.cleaning_dates
          for (let key in cleaningDates) {
            const cDate = cleaningDates[key].date
            this.cleaningDates.push(cDate)
          }
        }
        this.initDatePicker()
        this.isLoadingProfessionalCalendar = false
      },
      async fetchProfessionalCalendarJobs () {
        this.isLoadingProfessionalCalendarJobs = true
        this.availableTimes = []
        let response = await axios.get('professional/calendars/jobs', {
          params: {
            professional_id: this.selectedProfessional.id,
            cleaning_date: this.selectedDate
          }
        })
        this.isLoadingProfessionalCalendarJobs = false
        this.professionalCalendarJobs = null
        if (!response.data.error) {
          this.professionalCalendarJobs = response.data.calendar_jobs
        }
        this.availableTimes = this.getAvailableTimes(this.selectedDate)
      },
      async fetchSoldOutDates () {
        let response = await axios.get('professional/calendars/sold-out', {
          params: {
            professional_id: this.selectedProfessional.id,
            start_date: moment().format('YYYY-MM-DD'),
            work_hour: this.workHours
          }
        })
        this.fullyBookedDates = []
        if (!response.data.error) {
          this.fullyBookedDates = response.data.sold_out_dates
          this.initDatePicker()
        }
      },
      setAvailableTimes (nextAvailableTime) {
        this.availableTimes = []
        if (nextAvailableTime) {
          const arrTimes = this.globalState.arrFullTimes
          let startIndex = arrTimes.indexOf(nextAvailableTime)
          if (startIndex < 3) {
            startIndex = 3
          }
          for (let i = startIndex; i < arrTimes.length; i++) {
            this.availableTimes.push(arrTimes[i])
          }
        }
      },
      submitData (evt) {
        // Prevent modal from closing
        evt.preventDefault()
        let displayAlerDialog = false
        let cleaningDuration = this.globalState.placeSizes[this.selectedUserPlace.place_size_id].cleaning_duration
        if (this.order.service_id === 1 && this.order.work_hour < cleaningDuration) {
          displayAlerDialog = true
        }
        if (displayAlerDialog) {
          this.$swal({
            text: this.$i18n.t('message.no_datetime_booking.mismatched_accommodation_work_hours'),
            type: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: this.$i18n.t('message.button.yes'),
            cancelButtonText: this.$i18n.t('message.button.no')
          }).then((result) => {
            if (result) {
              this.updateOrderData()
            }
          })
        } else {
          this.updateOrderData()
        }
      },
      async updateOrderData () {
        this.isLoading = true
        const selectedDateTime = this.selectedDate + ' ' + this.selectedTime
        const cleaningDate = moment(selectedDateTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')
        const url = `/orders/${this.order.id}/update-service-datetime`
        const selectedProfessionalId = (this.selectedProfessional) ? this.selectedProfessional.id : 0
        const submitData = {
          user_place_id: this.selectedUserPlace.id,
          selected_professional_id: selectedProfessionalId,
          cleaning_date: cleaningDate
        }
        const { data } = await axios.post(url, submitData)
        this.isLoading = false
        if (!data.error) {
          this.showModal(false)
          this.$swal({text: this.$i18n.t('message.success'), type: 'success'})
          await this.loadOrderDetail(this.order.id)
          this.$emit('onUpdatedOrder', true)
        } else {
          await this.loadOrderDetail(this.order.id)
          this.$swal({text: data.message, type: 'error'})
          this.$emit('onUpdatedOrder', true)
        }
      }
    },
    async mounted () {
      this.language = this.$i18n.locale
      // Load global data to config disable same day booking
      const payload = {unlimited_package: 1, user_id: this.user.id}
      await this.$store.dispatch('globalVal/LOAD_GLOBAL_DATA', payload)
      this.parentServiceId = this.order.service_id
      if (this.globalState) {
        if (this.globalState.services[this.order.service_id].parent_id > 0) {
          this.parentServiceId = this.globalState.services[this.order.service_id].parent_id
        }
        if (this.globalState.configData) {
          this.sameDayBookingConfig = serviceHelpers.formatSameDayData(this.globalState.configData.same_day_booking)
        }
      }
    }
  }
</script>

<style scoped>
  @import '~air-datepicker/dist/css/datepicker.min.css';
</style>



// WEBPACK FOOTER //
// src/components/order/components/SelectServiceDateTimeModal.vue